import React, { useEffect, useState } from "react";
import { X, PlaneTakeoff, Clock } from "lucide-react";
import api from "../api";
import Portal from "./common/Portal";

export default function DoctorUnavailabilityModal({ doctor, onClose }) {
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!doctor) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.fetchDoctorExceptions(doctor.doctor_id);
        setExceptions(res.data || []);
      } catch (err) {
        console.error("Failed to fetch doctor unavailability:", err);
        setError(err.response?.data?.error || "Failed to load unavailability records.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [doctor]);

  if (!doctor) return null;

  return (
    <Portal>
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold flex items-center gap-1 bg-amber-100 text-amber-800">
                <PlaneTakeoff className="w-3.5 h-3.5" />
                Unavailability
              </span>
              <h3 className="text-lg font-bold text-gray-900">{doctor.name}</h3>
            </div>
            <p className="text-sm text-gray-500 mt-1">Recorded vacation / unavailability periods for this clinician.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="p-6 overflow-y-auto flex-1 bg-gray-50/50 space-y-2.5">
          {loading ? (
            <p className="text-sm text-gray-500 text-center py-6">Loading...</p>
          ) : error ? (
            <p className="text-sm text-red-500 text-center py-6">{error}</p>
          ) : exceptions.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">No vacation or unavailability recorded for this clinician.</p>
          ) : (
            exceptions.map((exc) => (
              <div key={exc.id} className="bg-white border border-gray-200 rounded-lg px-4 py-3">
                <div className="text-sm font-semibold text-gray-900">
                  {exc.start_date} → {exc.end_date}
                </div>
                {(exc.start_time || exc.end_time) && (
                  <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                    <Clock className="w-3 h-3" />
                    {exc.start_time || "00:00"} - {exc.end_time || "23:59"} (partial day)
                  </div>
                )}
                {exc.reason && <div className="text-xs text-gray-600 mt-1">{exc.reason}</div>}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );
}
